import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';

const GRID = 16;

function makeLit(n) { const s = new Set(); while (s.size < n) s.add(Math.floor(Math.random() * GRID)); return s; }

function GridMemoryGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [phase, setPhase] = useState('idle'); // idle|memorise|recall|feedback|gameover
  const [level, setLevel] = useState(1);
  const [lit, setLit] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [countdown, setCountdown] = useState(2);
  const [correct, setCorrect] = useState(null);
  const [best, setBest] = useStorage('gm_gridmemory_best', 0);
  const cdRef = useRef(null); const phaseRef = useRef(null);

  useEffect(() => () => { clearInterval(cdRef.current); clearTimeout(phaseRef.current); }, []);

  const startRound = (lvl) => {
    const newLit = makeLit(Math.min(lvl + 2, 10));
    setLit(newLit); setSelected(new Set()); setCorrect(null); setCountdown(2); setPhase('memorise');
    let cd = 2;
    cdRef.current = setInterval(() => { cd--; setCountdown(cd); if (cd <= 0) { clearInterval(cdRef.current); setPhase('recall'); } }, 1000);
  };

  const start = () => { setLevel(1); startRound(1); };

  const toggleCell = (i) => {
    if (phase !== 'recall') return;
    setSelected(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };

  const submit = () => {
    if (phase !== 'recall') return;
    const ok = [...lit].every(i => selected.has(i)) && selected.size === lit.size;
    setCorrect(ok); setPhase('feedback');
    if (ok) { phaseRef.current = setTimeout(() => { const nl2 = level + 1; setLevel(nl2); startRound(nl2); }, 900); }
    else {
      phaseRef.current = setTimeout(() => {
        saveGameScore('gridmemory', level); if (level > best) setBest(level); setPhase('gameover');
      }, 900);
    }
  };

  const cellBg = (i) => {
    if (phase === 'memorise' && lit.has(i)) return _T.accent;
    if (phase === 'recall' && selected.has(i)) return `${_T.accent}99`;
    if (phase === 'feedback') {
      if (lit.has(i) && selected.has(i)) return '#4AC87A';
      if (lit.has(i)) return '#4AC87A66';
      if (selected.has(i)) return C.red;
    }
    return _T.bg3;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>■ {nl ? 'Stop' : 'Stop'}</button>
      </div>

      {phase === 'idle' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🔲</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Rastergeheugen' : 'Grid Memory'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.7 }}>
            {nl ? 'Onthoud welke vakjes oplichten, tik ze daarna aan. Elke ronde komt er één bij.' : 'Remember which cells light up, then tap them. Each correct round adds one more.'}
          </div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, margin: '12px 0' }}>🏆 Best: {nl ? 'niveau' : 'level'} {best}</div>}
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {(phase === 'memorise' || phase === 'recall' || phase === 'feedback') && (
        <div>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 12 }), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: C.silver }}>{nl ? 'Niveau' : 'Level'} <span style={{ color: C.white, fontWeight: 700 }}>{level}</span></div>
            <div style={{ fontSize: 13, color: C.silver }}>{lit.size} {nl ? 'vakjes' : 'cells'}</div>
            {phase === 'memorise' && <div style={{ fontSize: 13, color: _T.accent, fontWeight: 700 }}>{countdown}s</div>}
            {phase === 'recall' && <div style={{ fontSize: 12, color: C.silver }}>{nl ? 'Tik de vakjes' : 'Tap cells'}</div>}
            {phase === 'feedback' && <div style={{ fontSize: 18 }}>{correct ? '✓' : '✗'}</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
            {Array.from({ length: GRID }, (_, i) => (
              <div key={i} onClick={() => toggleCell(i)} style={{ width: '100%', aspectRatio: '1', borderRadius: 8, border: `1px solid ${_T.cardBorderBot}`, background: cellBg(i), cursor: phase === 'recall' ? 'pointer' : 'default', transition: 'background 0.15s', boxShadow: phase === 'memorise' && lit.has(i) ? `0 0 14px ${_T.accent}88` : 'none' }} />
            ))}
          </div>
          {phase === 'recall' && <button style={s_PB({ width: '100%' })} onClick={submit}>{nl ? 'Bevestigen' : 'Submit'}</button>}
          {phase === 'memorise' && <div style={{ textAlign: 'center', color: C.silver, fontSize: 12 }}>{nl ? 'Onthoud de verlichte vakjes…' : 'Memorise the lit cells…'}</div>}
        </div>
      )}

      {phase === 'gameover' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔲</div>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700 }}>{nl ? 'Niveau' : 'Level'} {level}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>{nl ? 'bereikt' : 'reached'}</div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, marginBottom: 12 }}>🏆 Best: {nl ? 'niveau' : 'level'} {best}</div>}
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default GridMemoryGame;
